import React, { useState, useEffect } from 'react';
import useFetchData from "repo/get";
import usePutData from "../../../../repo/put";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Accordion, AccordionTab } from 'primereact/accordion';
import { InputNumber } from "primereact/inputnumber";

const CardCidades = () => {
  const [error] = useState(null);
  const [selectedDeliverer, setSelectedDeliverer] = useState(null);
  const [isDialogVisible, setIsDialogVisible] = useState(false); 
  // Estado para armazenar os entregadores localmente
  const [localDeliverers, setLocalDeliverers] = useState([]);
  // Estado para armazenar alterações no prazo de cada cidade
  const [editedPrazos, setEditedPrazos] = useState({});
  
  const { data, loading } = useFetchData("api/cidades/findAll");
  const { putData } = usePutData();

  // Inicializa o estado local com os entregadores filtrados quando os dados são carregados
  useEffect(() => {
    if (!loading && Array.isArray(data)) {
      const filtered = data.filter(deliverer => deliverer.frete !== true);
      setLocalDeliverers(filtered);
    }
  }, [data, loading]);

  if (error) {
    return <p>{error}</p>;
  }

  if (loading) {
    return <p>Carregando...</p>;
  }

  // Agrupa os entregadores pela propriedade regiao.nome usando o estado local
  const groupedByRegion = localDeliverers.reduce((acc, deliverer) => {
    const regionName = deliverer.regiao.nome;
    if (!acc[regionName]) {
      acc[regionName] = [];
    }
    acc[regionName].push(deliverer);
    return acc;
  }, {});

  const handleShowDetails = (deliverer) => {
    setSelectedDeliverer(deliverer);
    setIsDialogVisible(true); 
  };

  const handleCloseDialog = () => {
    setIsDialogVisible(false); 
    setSelectedDeliverer(null);
  };

  // Atualiza o prazo modificado para o entregador utilizando e.value
  const handlePrazoChange = (e, deliverer) => {
    const newValue = e.value;
    setEditedPrazos(prev => ({
      ...prev,
      [deliverer.id]: newValue
    }));
  };

  // Função para atualizar o prazo via PUT e atualizar a interface com a resposta da API
  const handleUpdatePrazo = async (deliverer) => {
    const newPrazo = editedPrazos[deliverer.id];
    try {
      const response = await putData(`api/cidades/updateprazo/${deliverer.id}`, {
        prazo: newPrazo
      });
      console.log("Prazo atualizado:", response);
      // Atualiza o estado local dos entregadores para refletir o novo prazo
      setLocalDeliverers(prev =>
        prev.map(d => d.id === deliverer.id ? { ...d, prazo: newPrazo } : d)
      );
      // Remove a alteração do estado de edição, pois já foi aplicada
      setEditedPrazos(prev => {
        const updated = { ...prev };
        delete updated[deliverer.id];
        return updated;
      });
    } catch (error) {
      console.error("Erro ao atualizar prazo:", error);
    }
  };

  return (
    <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-white p-6">
        <h1 className="text-2xl font-bold text-blueGray-700">Lista por Região</h1>
      <div className="p-6 pb-0 mb-0 border-b-0 border-b-solid rounded-t-2xl border-b-transparent">
      </div>
    <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-white p-6">
      
      <Accordion multiple>
        {Object.keys(groupedByRegion).sort().map((regionName, idx) => (
          <AccordionTab header={regionName} key={idx}>
            <div className="flex-auto px-0 pt-0 pb-2">
              <div className="p-0 overflow-x-auto">
                <table className="items-center w-full mb-0 align-top border-collapse dark:border-white/40 text-slate-500">
                  <thead className="align-bottom">
                    <tr>
                      <th className="px-6 py-3 font-bold text-left uppercase align-middle bg-transparent border-b border-collapse shadow-none dark:border-white/40 dark:text-white text-xxs border-b-solid tracking-none whitespace-nowrap text-slate-400 opacity-70">
                        Cidades
                      </th>
                      <th className="px-6 py-3 pl-2 font-bold text-left uppercase align-middle bg-transparent border-b border-collapse shadow-none dark:border-white/40 dark:text-white text-xxs border-b-solid tracking-none whitespace-nowrap text-slate-400 opacity-70">
                        Prazo
                      </th>
                      <th className="px-6 py-3 pl-2 font-bold text-left uppercase align-middle bg-transparent border-b border-collapse shadow-none dark:border-white/40 dark:text-white text-xxs border-b-solid tracking-none whitespace-nowrap text-slate-400 opacity-70">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedByRegion[regionName].map((deliverer, index) => {
                      // Se houver alteração, usa o valor editado; caso contrário, o prazo original
                      const inputValue = editedPrazos[deliverer.id] ?? deliverer.prazo;
                      // Exibe o botão somente se o valor editado for diferente do original
                      const hasChanged = inputValue !== deliverer.prazo;
                      return (
                        <tr key={index}>
                          <td className="p-2 align-middle bg-transparent border-b dark:border-white/40 whitespace-nowrap shadow-transparent">
                            <div className="flex px-2 py-1">
                              <div className="mr-2 flex h-8 w-8 items-center justify-center rounded-lg bg-center stroke-0 text-center xl:p-2.5">
                                <i className="relative top-0 text-sm leading-normal text-slate-700 ni ni-single-02"></i>
                              </div>
                              <div className="flex flex-col justify-center">
                                <h6 className="mb-0 text-sm leading-normal dark:text-white font-semibold">
                                  {deliverer.nome}
                                </h6>
                                <p className="mb-0 text-xs leading-tight dark:text-white dark:opacity-80 text-slate-400">
                                  {deliverer.regiao.nome}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="p-2 align-middle bg-transparent border-b dark:border-white/40 whitespace-nowrap shadow-transparent">
                            <InputNumber
                              value={inputValue}
                              onChange={(e) => handlePrazoChange(e, deliverer)}
                              mode="decimal"
                              min={0}
                              max={8}
                              className="w-full p-2 text-xs font-semibold leading-tight dark:text-white dark:opacity-80"
                            />
                          </td>
                          <td className="p-2 align-middle bg-transparent border-b dark:border-white/40 whitespace-nowrap shadow-transparent">
                            {hasChanged && (
                              <Button
                                label="Mudar Prazo"
                                icon="pi pi-clock"
                                onClick={() => handleUpdatePrazo(deliverer)}
                                className="bg-lightBlue-500 text-white active:bg-lightBlue-600 px-3 py-1 rounded-md shadow font-bold"
                              />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </AccordionTab>
        ))}
      </Accordion>
      <Dialog header="Detalhes" visible={isDialogVisible} style={{ width: '50vw' }} onHide={handleCloseDialog}>
        {selectedDeliverer && (
          <div>
            <p><strong>Nome:</strong> {selectedDeliverer.nome}</p>
            <p><strong>Região:</strong> {selectedDeliverer.regiao.nome}</p>
            <p><strong>Prazo:</strong> {selectedDeliverer.prazo} Dia(s)</p>
          </div>
        )}
      </Dialog>
    </div>
    </div>
  );
};

export default CardCidades;
